import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type {
  MutualFund,
  MutualFundFormData,
  MutualFundNavEntry,
} from '@/types/mutualFund';

const QUERY_KEY = 'mutual-funds';
const NAV_QUERY_KEY = 'mutual-fund-nav-history';

// ---------------------------------------------------------------------------
// Read hooks
// ---------------------------------------------------------------------------

/**
 * Fetches all mutual fund investment accounts for the current user,
 * joined with their metadata.
 */
export function useMutualFunds() {
  const { user } = useAuth();

  return useQuery({
    queryKey: [QUERY_KEY, user?.id],
    queryFn: async () => {
      const { data: types, error: typesError } = await supabase
        .from('investment_types' as any)
        .select('id')
        .eq('name', 'Mutual Fund');

      if (typesError) throw typesError;
      const typeIds = (types as { id: string }[]).map((t) => t.id);

      const { data, error } = await supabase
        .from('investment_accounts' as any)
        .select(`
          *,
          investment_type:investment_types(*),
          mutual_fund_metadata(*)
        `)
        .in('investment_type_id', typeIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data as unknown[]).map((row: any) => ({
        ...row,
        mutual_fund_metadata: Array.isArray(row.mutual_fund_metadata)
          ? row.mutual_fund_metadata[0] ?? null
          : row.mutual_fund_metadata ?? null,
      })) as MutualFund[];
    },
    enabled: !!user,
  });
}

/**
 * Fetches NAV history for a specific mutual fund, ordered by date ascending
 * so it can be fed directly into a Recharts line chart.
 */
export function useMutualFundNavHistory(investmentAccountId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: [NAV_QUERY_KEY, investmentAccountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mutual_fund_nav_history' as any)
        .select('*')
        .eq('investment_account_id', investmentAccountId!)
        .order('nav_date', { ascending: true });

      if (error) throw error;
      return data as MutualFundNavEntry[];
    },
    enabled: !!user && !!investmentAccountId,
  });
}

// ---------------------------------------------------------------------------
// Write hooks
// ---------------------------------------------------------------------------

/**
 * Creates a new mutual fund investment account with its metadata.
 * Rolls back the investment account if metadata insertion fails.
 */
export function useCreateMutualFund() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (formData: MutualFundFormData) => {
      // Resolve the Mutual Fund investment type id.
      const { data: typeRows, error: typeError } = await supabase
        .from('investment_types' as any)
        .select('id')
        .eq('name', 'Mutual Fund')
        .single();

      if (typeError) throw typeError;
      const investmentTypeId = (typeRows as { id: string }).id;

      // Create the base investment account.
      const { data: account, error: accountError } = await supabase
        .from('investment_accounts' as any)
        .insert({
          user_id: user!.id,
          investment_type_id: investmentTypeId,
          fund_name: formData.fund_name,
          fund_manager: formData.fund_manager,
          account_number: formData.account_number,
          currency: formData.currency,
          interest_rate: formData.interest_rate,
          starting_amount: formData.starting_amount,
          balance: formData.starting_amount,
          notes: formData.notes ?? null,
          description: formData.description ?? null,
        })
        .select()
        .single();

      if (accountError) throw accountError;
      const { id: accountId } = account as { id: string };

      // Create the mutual fund metadata.
      const { error: metaError } = await supabase
        .from('mutual_fund_metadata' as any)
        .insert({
          investment_account_id: accountId,
          fund_category: formData.fund_category,
          risk_rating: formData.risk_rating,
          benchmark_index: formData.benchmark_index ?? null,
          isin: formData.isin ?? null,
          units_held: formData.units_held,
          unit_cost_basis: formData.unit_cost_basis,
          current_nav: formData.current_nav,
          nav_date: formData.nav_date ?? null,
        });

      if (metaError) {
        // Rollback: remove the account if metadata fails.
        await supabase
          .from('investment_accounts' as any)
          .delete()
          .eq('id', accountId);
        throw metaError;
      }

      // Seed the first NAV history entry if nav_date is provided.
      if (formData.nav_date && formData.current_nav > 0) {
        await supabase.from('mutual_fund_nav_history' as any).insert({
          investment_account_id: accountId,
          nav_per_unit: formData.current_nav,
          nav_date: formData.nav_date,
        });
      }

      return account;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Updates an existing mutual fund investment account and its metadata.
 */
export function useUpdateMutualFund() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<MutualFundFormData> & { id: string }) => {
      // Update base investment account fields.
      const accountUpdates: Record<string, unknown> = {};
      if (updates.fund_name !== undefined) accountUpdates.fund_name = updates.fund_name;
      if (updates.fund_manager !== undefined) accountUpdates.fund_manager = updates.fund_manager;
      if (updates.account_number !== undefined) accountUpdates.account_number = updates.account_number;
      if (updates.interest_rate !== undefined) accountUpdates.interest_rate = updates.interest_rate;
      if (updates.notes !== undefined) accountUpdates.notes = updates.notes;
      if (updates.description !== undefined) accountUpdates.description = updates.description;

      if (Object.keys(accountUpdates).length > 0) {
        const { error } = await supabase
          .from('investment_accounts' as any)
          .update(accountUpdates)
          .eq('id', id);
        if (error) throw error;
      }

      // Update metadata fields.
      const metaUpdates: Record<string, unknown> = {};
      if (updates.fund_category !== undefined) metaUpdates.fund_category = updates.fund_category;
      if (updates.risk_rating !== undefined) metaUpdates.risk_rating = updates.risk_rating;
      if (updates.benchmark_index !== undefined) metaUpdates.benchmark_index = updates.benchmark_index;
      if (updates.isin !== undefined) metaUpdates.isin = updates.isin || null;
      if (updates.units_held !== undefined) metaUpdates.units_held = updates.units_held;
      if (updates.unit_cost_basis !== undefined) metaUpdates.unit_cost_basis = updates.unit_cost_basis;
      if (updates.current_nav !== undefined) metaUpdates.current_nav = updates.current_nav;
      if (updates.nav_date !== undefined) metaUpdates.nav_date = updates.nav_date;

      if (Object.keys(metaUpdates).length > 0) {
        const { error } = await supabase
          .from('mutual_fund_metadata' as any)
          .update(metaUpdates)
          .eq('investment_account_id', id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Deletes a mutual fund investment account.
 * Metadata and NAV history are removed via CASCADE.
 */
export function useDeleteMutualFund() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('investment_accounts' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Records a new NAV entry for a mutual fund and updates current_nav in metadata.
 * Upserts on (investment_account_id, nav_date) so re-recording the same day
 * just updates the price.
 */
export function useRecordNav() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      investmentAccountId,
      navPerUnit,
      navDate,
    }: {
      investmentAccountId: string;
      navPerUnit: number;
      navDate: string;
    }) => {
      const { error: historyError } = await supabase
        .from('mutual_fund_nav_history' as any)
        .upsert(
          {
            investment_account_id: investmentAccountId,
            nav_per_unit: navPerUnit,
            nav_date: navDate,
          },
          { onConflict: 'investment_account_id,nav_date' }
        );
      if (historyError) throw historyError;

      // Keep current_nav in metadata in sync.
      const { error: metaError } = await supabase
        .from('mutual_fund_metadata' as any)
        .update({ current_nav: navPerUnit, nav_date: navDate })
        .eq('investment_account_id', investmentAccountId);
      if (metaError) throw metaError;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [NAV_QUERY_KEY, vars.investmentAccountId] });
    },
  });
}
